import React, { useRef, useEffect } from 'react';
import { worldPolygons } from './worldPolygons';

// Mercator projection
function project(lat, lng, W, H) {
  const mapAspect = 2;
  const viewAspect = W / H;
  let mapW, mapH, offX, offY;
  if (viewAspect > mapAspect) {
    mapH = H * 0.85;
    mapW = mapH * mapAspect;
    offX = (W - mapW) / 2;
    offY = H * 0.075;
  } else {
    mapW = W * 0.92;
    mapH = mapW / mapAspect;
    offX = W * 0.04;
    offY = (H - mapH) / 2;
  }
  const x = (lng + 180) / 360 * mapW + offX;
  const latRad = lat * Math.PI / 180;
  const mercY = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = mapH * 0.5 + offY - mercY * mapH * 0.155;
  return { x, y };
}

// Ray-casting point-in-polygon
function pointInPoly(testLng, testLat, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const lngI = poly[i][0], latI = poly[i][1];
    const lngJ = poly[j][0], latJ = poly[j][1];
    if ((latI > testLat) !== (latJ > testLat) &&
        testLng < (lngJ - lngI) * (testLat - latI) / (latJ - latI) + lngI) {
      inside = !inside;
    }
  }
  return inside;
}

const cities = [
  { name: 'New York',  lat: 40.7,  lng: -74,   color: '#6366f1', size: 4.5 },
  { name: 'London',    lat: 51.5,  lng: -0.1,  color: '#6366f1', size: 4.5 },
  { name: 'Tokyo',     lat: 35.7,  lng: 139.7, color: '#d3af34', size: 4.5 },
  { name: 'Sydney',    lat: -33.9, lng: 151.2, color: '#10b981', size: 3.5 },
  { name: 'Singapore', lat: 1.35,  lng: 103.8, color: '#10b981', size: 3.5 },
  { name: 'Frankfurt', lat: 50.1,  lng: 8.7,   color: '#6366f1', size: 3.5 },
  { name: 'Hong Kong', lat: 22.3,  lng: 114.2, color: '#d3af34', size: 3.5 },
  { name: 'Dubai',     lat: 25.2,  lng: 55.3,  color: '#d3af34', size: 3 },
  { name: 'Mumbai',    lat: 19.1,  lng: 72.9,  color: '#10b981', size: 3 },
  { name: 'São Paulo', lat: -23.5, lng: -46.6, color: '#10b981', size: 3 },
  { name: 'Chicago',   lat: 41.9,  lng: -87.6, color: '#6366f1', size: 3 },
  { name: 'Shanghai',  lat: 31.2,  lng: 121.5, color: '#d3af34', size: 3.5 },
];

const routes = [
  [0, 1], [1, 5], [1, 7], [7, 8], [8, 4], [4, 6],
  [6, 2], [2, 11], [4, 3], [0, 10], [0, 9], [6, 11], [1, 2],
];

const WorldMapCanvas = ({ style }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef({ mapDots: [], pulses: [], particles: [], frame: 0, W: 0, H: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const state = stateRef.current;
    let animId;

    // Init particles
    state.particles = [];
    for (let i = 0; i < 30; i++) {
      state.particles.push({
        x: Math.random() * 2000, y: Math.random() * 1200,
        vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.15,
        size: Math.random() * 1.2 + 0.4, opacity: Math.random() * 0.15 + 0.03,
      });
    }
    state.pulses = [];

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      state.W = parent.clientWidth;
      state.H = parent.clientHeight;
      canvas.width = state.W * dpr;
      canvas.height = state.H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildMapDots();
    }

    function buildMapDots() {
      state.mapDots = [];
      const step = 2;
      for (let lat = -60; lat <= 72; lat += step) {
        for (let lng = -170; lng <= 180; lng += step) {
          for (const poly of worldPolygons) {
            if (pointInPoly(lng, lat, poly)) {
              const { x, y } = project(lat, lng, state.W, state.H);
              if (x >= 0 && x <= state.W && y >= 0 && y <= state.H) {
                state.mapDots.push({
                  x, y,
                  brightness: 0.12 + Math.random() * 0.18,
                  phase: Math.random() * Math.PI * 2,
                });
              }
              break;
            }
          }
        }
      }
    }

    function spawnPulse() {
      const route = routes[Math.floor(Math.random() * routes.length)];
      const from = cities[route[0]], to = cities[route[1]];
      const rev = Math.random() > 0.5;
      state.pulses.push({
        from: rev ? to : from, to: rev ? from : to,
        progress: 0, speed: 0.002 + Math.random() * 0.003,
        color: from.color,
      });
    }

    function draw() {
      const { W, H } = state;
      ctx.clearRect(0, 0, W, H);
      state.frame++;
      const frame = state.frame;

      if (frame % 50 === 0) spawnPulse();
      if (frame % 80 === 0) spawnPulse();

      // Map dots
      for (const dot of state.mapDots) {
        const b = dot.brightness + Math.sin(frame * 0.015 + dot.phase) * 0.06;
        ctx.fillStyle = `rgba(100, 140, 200, ${b})`;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, 1.1, 0, Math.PI * 2);
        ctx.fill();
      }

      // Route lines (curved)
      for (const [i, j] of routes) {
        const from = project(cities[i].lat, cities[i].lng, W, H);
        const to = project(cities[j].lat, cities[j].lng, W, H);
        const mx = (from.x + to.x) / 2;
        const my = (from.y + to.y) / 2 - Math.abs(from.x - to.x) * 0.12;
        ctx.strokeStyle = 'rgba(99, 102, 241, 0.06)';
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(mx, my, to.x, to.y);
        ctx.stroke();
      }

      // Traveling pulses
      for (let p = state.pulses.length - 1; p >= 0; p--) {
        const pulse = state.pulses[p];
        pulse.progress += pulse.speed;
        if (pulse.progress > 1) { state.pulses.splice(p, 1); continue; }
        const from = project(pulse.from.lat, pulse.from.lng, W, H);
        const to = project(pulse.to.lat, pulse.to.lng, W, H);
        const mx = (from.x + to.x) / 2;
        const my = (from.y + to.y) / 2 - Math.abs(from.x - to.x) * 0.12;
        const t = pulse.progress;
        const x = (1-t)*(1-t)*from.x + 2*(1-t)*t*mx + t*t*to.x;
        const y = (1-t)*(1-t)*from.y + 2*(1-t)*t*my + t*t*to.y;

        const g = ctx.createRadialGradient(x, y, 0, x, y, 10);
        g.addColorStop(0, pulse.color + '88');
        g.addColorStop(0.5, pulse.color + '22');
        g.addColorStop(1, 'transparent');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();

        for (let s = 1; s <= 4; s++) {
          const tt = Math.max(0, t - s * 0.015);
          const tx = (1-tt)*(1-tt)*from.x + 2*(1-tt)*tt*mx + tt*tt*to.x;
          const ty = (1-tt)*(1-tt)*from.y + 2*(1-tt)*tt*my + tt*tt*to.y;
          ctx.fillStyle = `rgba(255,255,255,${Math.max(0, 0.4 - s * 0.1)})`;
          ctx.beginPath();
          ctx.arc(tx, ty, 1.2 - s * 0.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // City nodes
      for (let ci = 0; ci < cities.length; ci++) {
        const city = cities[ci];
        const { x, y } = project(city.lat, city.lng, W, H);

        // Pulsing ring
        const ps = 1 + Math.sin(frame * 0.03 + ci * 1.2) * 0.4;
        const po = 0.12 + Math.sin(frame * 0.03 + ci * 1.2) * 0.06;
        ctx.strokeStyle = city.color;
        ctx.globalAlpha = po;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.arc(x, y, city.size * 2.5 * ps, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Glow
        const glow = ctx.createRadialGradient(x, y, 0, x, y, city.size * 3.5);
        glow.addColorStop(0, city.color + '30');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, city.size * 3.5, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = city.color;
        ctx.beginPath();
        ctx.arc(x, y, city.size, 0, Math.PI * 2);
        ctx.fill();

        // Bright center
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.beginPath();
        ctx.arc(x, y, city.size * 0.35, 0, Math.PI * 2);
        ctx.fill();

        // City label
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '10px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(city.name, x, y - city.size * 3 - 4);
      }

      // Floating particles
      for (const p of state.particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.fillStyle = `rgba(99, 102, 241, ${p.opacity})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        ...style,
      }}
    />
  );
};

export default WorldMapCanvas;
